import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import React, { Dispatch, SetStateAction } from "react";
import { Box, Button, Typography } from "@mui/material";

import List from "../../../components/List";
import { tl } from "../../../components/translate";
import { getFullName } from "../../../helpers/formatters";
import PageControl from "../../../components/PageControl";
import useMobileScreen from "../../../hooks/useMobileScreen";
import { Client } from "../../../interfaces/ClientInterface";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import OpdRequestClientSearch from "../OpdRequest/OpdRequestClientSearch";
import { DentalLabOrder } from "../../../interfaces/DentalLabOrder";
import OpdRequestStatusChip from "../OpdRequest/OpdRequestStatusChip";

interface DentalLabOrderListProps {
  selectedRow: Partial<DentalLabOrder> | null;
  setSelectedRow: Dispatch<SetStateAction<Partial<DentalLabOrder> | null>>;
  dentalLabOrderData: { results: DentalLabOrder[]; total: number } | null;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  selectedClient: Partial<Client> | null;
  setSelectedClient: Dispatch<SetStateAction<Partial<Client> | null>>;
  isListForReport?: boolean;
  hideCreateButton?: boolean;
  doNotHandleRowClick?: boolean;
}

export default function DentalLabOrderList(props: DentalLabOrderListProps): React.JSX.Element {
  const pageSize = 20;
  const dispatch = useDispatch();
  const isMobileScreen = useMobileScreen();
  const {
    doNotHandleRowClick,
    selectedRow,
    setSelectedRow,
    dentalLabOrderData,
    page,
    setPage,
    selectedClient,
    setSelectedClient,
    isListForReport,
    hideCreateButton
  } = props;

  const customButtonGroup = !hideCreateButton && (
    <Button
      variant="contained"
      onClick={() => dispatch(push("/assessment/dentalLabOrders/create"))}
    >
      {isMobileScreen ? tl("create") : "Create Dental Lab Order"}
    </Button>
  );

  return (
    <Box height="calc(100% - 30px)">
      <List
        automation="dentalLabOrder"
        data={dentalLabOrderData?.results || []}
        rowHeight={50}
        hideCreateButton
        withoutSearch
        defaultSortColumn="id"
        defaultSortOrder={-1}
        isResponsive
        activeRow={selectedRow && selectedRow.id}
        onRowClick={(row) => {
          if (isListForReport) {
            if (selectedRow && Number(selectedRow.id) === Number(row.id)) {
              setSelectedRow(null);
            } else {
              setSelectedRow(row as Partial<DentalLabOrder>);
            }
            return;
          }
          const unselect = selectedRow && Number(selectedRow.id) === Number(row.id);
          setSelectedRow(unselect ? null : (row as Partial<DentalLabOrder>));
          if (!doNotHandleRowClick) {
            if (unselect) {
              dispatch(push(`/assessment/dentalLabOrders`));
            } else {
              dispatch(push(`/assessment/dentalLabOrders/${row.id}`));
            }
          }
        }}
        customButtonGroup={!isListForReport ? customButtonGroup : undefined}
        additionalHeaderFilters={
          !isListForReport ? (
            <OpdRequestClientSearch
              selectedClient={selectedClient}
              setSelectedClient={setSelectedClient}
            />
          ) : undefined
        }
        columns={[
          {
            key: "clientName",
            label: "Client",
            sortable: true,
            sortFunction: (a, b) =>
              a.client.firstName.toLowerCase() > b.client.firstName.toLowerCase() ? 1 : -1,
            formatter: (row) => (
              <Typography component="div" variant="body2">
                <Box>{getFullName(row.client)}</Box>
              </Typography>
            )
          },
          {
            key: "teethNumbers",
            label: "Teeth Numbers",
            formatter: (row) => (
              <Typography component="div" variant="body2">
                <Box>{(row.teethNumbers ?? row.teeth ?? []).join(", ")}</Box>
              </Typography>
            )
          },
          {
            key: "restorationTypes",
            label: "Restoration Types",
            formatter: (row) => (
              <Typography component="div" variant="body2">
                <Box>{(row.restorationTypes ?? row.items ?? []).join(", ")}</Box>
              </Typography>
            )
          },
          {
            key: "shadeDetails",
            label: "Shades",
            formatter: (row) => (
              <Typography component="div" variant="body2">
                <Box>{row.shadeDetails?.length ? row.shadeDetails.join(", ") : "-"}</Box>
              </Typography>
            )
          },
          {
            key: "occlusalStains",
            label: "Occlusal Stains",
            formatter: (row) => (
              <Typography component="div" variant="body2">
                <Box>{row.occlusalStains?.length ? row.occlusalStains.join(", ") : "-"}</Box>
              </Typography>
            )
          },
          {
            key: "ponticDesigns",
            label: "Pontic Designs",
            formatter: (row) => (
              <Typography component="div" variant="body2">
                <Box>{row.ponticDesigns?.length ? row.ponticDesigns.join(", ") : "-"}</Box>
              </Typography>
            )
          },
          {
            key: "orderDate",
            label: "Order Date",
            sortable: true,
            formatter: (row) => (
              <Typography component="div" variant="body2">
                <Box>{convertADtoBS(new Date(row.orderDate)).formatted4}</Box>
              </Typography>
            )
          },
          {
            key: "deliveryDate",
            label: "Delivery Date",
            sortable: true,
            formatter: (row) => (
              <Typography component="div" variant="body2">
                <Box>
                  {row.deliveryDate ? convertADtoBS(new Date(row.deliveryDate)).formatted4 : "-"}
                </Box>
              </Typography>
            )
          },
          {
            key: "status",
            label: "Status",
            sortable: false,
            formatter: (row) => <OpdRequestStatusChip status={row.status} />
          }
        ]}
      />
      {!isListForReport && (
        <Box height="40px" borderTop="1px solid lightgray" display="flex" justifyContent="flex-end">
          <Box width="200px">
            <PageControl
              page={page}
              onSetPage={(val) => {
                setPage(val);
              }}
              pageSize={pageSize}
              maximumDataCount={dentalLabOrderData?.total || 0}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
